import type { TableBlock } from '@atlas/renderer';
import type { KeyboardEvent } from 'react';
import { Editor, Transforms, Element, Range } from 'slate';
import locateTextBefore from '@atlas/tool/box/slate/locateTextBefore';
import matchNode from '@atlas/tool/box/slate/matchNode';
import hotkey from '@atlas/tool/box/event/hotkey';
import createTextBlock from './createTextBlock';
import changeEditorSelection from '@atlas/tool/box/slate/changeEditorSelection';

export default function handleTableNavigationKeyDownEvent(editor: Editor, event: KeyboardEvent): boolean {
    if (!editor.selection) return false;

    const tableNode = matchNode<TableBlock>(editor, (node) => Element.isElement(node) && node.type === 'table');
    if (!tableNode) return false;

    if (hotkey('enter', 'hidden').matches(event)) {
        event.preventDefault();

        Transforms.insertNodes(
            editor,
            { type: 'paragraph', children: [{ text: '' }] },
            { at: [editor.selection.focus.path[0] + 1] },
        );
        changeEditorSelection(editor, {
            anchor: { path: [editor.selection.focus.path[0] + 1], offset: 0 },
            focus: { path: [editor.selection.focus.path[0] + 1], offset: 0 },
        });
        return true;
    }

    if (hotkey('tab', 'hidden').matches(event)) {
        event.preventDefault();
        const [tableIndex, rowIndex, cellIndex] = editor.selection.focus.path;

        if (cellIndex < tableNode.children[rowIndex].children.length - 1) {
            const location = Editor.start(editor, [tableIndex, rowIndex, cellIndex + 1]);
            changeEditorSelection(editor, {
                anchor: location,
                focus: location,
            });
        } else if (rowIndex < tableNode.children.length - 1) {
            const location = Editor.start(editor, [tableIndex, rowIndex + 1]);
            changeEditorSelection(editor, {
                anchor: location,
                focus: location,
            });
        } else if (tableIndex < editor.children.length - 1) {
            const location = Editor.start(editor, [tableIndex + 1]);
            changeEditorSelection(editor, {
                anchor: location,
                focus: location,
            });
        }
        return true;
    }

    if (hotkey('shift+tab', 'hidden').matches(event)) {
        event.preventDefault();
        const [tableIndex, rowIndex, cellIndex] = editor.selection.focus.path;

        if (cellIndex > 0) {
            const location = Editor.end(editor, [tableIndex, rowIndex, cellIndex - 1]);
            changeEditorSelection(editor, {
                anchor: location,
                focus: location,
            });
        } else if (rowIndex > 0) {
            const location = Editor.end(editor, [tableIndex, rowIndex - 1]);
            changeEditorSelection(editor, {
                anchor: location,
                focus: location,
            });
        } else if (tableIndex > 0) {
            const location = Editor.end(editor, [tableIndex - 1]);
            changeEditorSelection(editor, {
                anchor: location,
                focus: location,
            });
        }

        return true;
    }

    if (
        (hotkey('up', 'hidden').matches(event) || hotkey('down', 'hidden').matches(event)) &&
        Range.isCollapsed(editor.selection)
    ) {
        const { focus } = editor.selection;
        const [tableIndex, rowIndex, cellIndex] = focus.path;
        const cellPath = [tableIndex, rowIndex, cellIndex];

        if (event.key === 'ArrowUp' && locateTextBefore(editor, '\n')) return false;
        if (event.key === 'ArrowDown') {
            const textAfterCursor = Editor.string(editor, {
                focus,
                anchor: Editor.end(editor, cellPath),
            });
            if (textAfterCursor.includes('\n')) return false;
        }

        event.preventDefault();
        const shift = event.key === 'ArrowUp' ? -1 : 1;
        const newRowIndex = rowIndex + shift;

        if (newRowIndex < 0) {
            if (tableIndex === 0) {
                const start = Editor.start(editor, [0]);
                changeEditorSelection(editor, { anchor: start, focus: start });
            } else {
                const location = Editor.end(editor, [tableIndex - 1]);
                changeEditorSelection(editor, {
                    anchor: location,
                    focus: location,
                });
            }
            return true;
        }

        if (tableNode.children.length <= newRowIndex) {
            if (editor.children.length <= tableIndex + 1) {
                Transforms.insertNodes(editor, [createTextBlock()], { at: [tableIndex + 1] });
            }

            const nextBlockStart = Editor.start(editor, [tableIndex + 1]);
            changeEditorSelection(editor, { anchor: nextBlockStart, focus: nextBlockStart });

            return true;
        }

        const newRowCellStart = Editor.start(editor, [tableIndex, newRowIndex, cellIndex]);
        changeEditorSelection(editor, { anchor: newRowCellStart, focus: newRowCellStart });

        return true;
    }

    return false;
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('handleTableNavigationKeyDownEvent', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
